import { api } from './api';

class ImmigrationConsultancyService {
  // Countries
  async getCountries() {
    const response = await api.get('/immigration/countries/');
    return response.data.results || [];
  }

  async getCountry(countryId) {
    const response = await api.get(`/immigration/countries/${countryId}/`);
    return response.data;
  }

  // Visa Types
  async getVisaTypes(filters = {}) {
    const params = new URLSearchParams();
    if (filters.country) params.append('country', filters.country);
    if (filters.category) params.append('category', filters.category);
    if (filters.popular) params.append('popular', filters.popular);
    
    const response = await api.get(`/immigration/visa-types/?${params}`);
    return response.data.results || [];
  }

  async getVisaType(identifier) {
    // Handle both slug and ID lookups
    const response = await api.get(`/immigration/visa-types/${identifier}/`);
    return response.data;
  }

  async getVisaRequirements(visaTypeId) {
    const response = await api.get(`/immigration/visa-types/${visaTypeId}/requirements/`);
    return response.data.results || [];
  }

  // Immigration Policies
  async getPolicies(filters = {}) {
    const params = new URLSearchParams();
    if (filters.country) params.append('country', filters.country);
    if (filters.visa_type) params.append('visa_type', filters.visa_type);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.featured) params.append('featured', filters.featured);
    if (filters.recent) params.append('recent', filters.recent);
    
    const response = await api.get(`/immigration/policies/?${params}`);
    return response.data.results || [];
  }

  async getPolicy(identifier) {
    // Handle both slug and ID lookups
    const response = await api.get(`/immigration/policies/${identifier}/`);
    return response.data;
  }

  // Immigration Guides
  async getGuides(filters = {}) {
    const params = new URLSearchParams();
    if (filters.country) params.append('country', filters.country);
    if (filters.visa_type) params.append('visa_type', filters.visa_type);
    if (filters.difficulty) params.append('difficulty', filters.difficulty);
    if (filters.featured) params.append('featured', filters.featured);
    
    const response = await api.get(`/immigration/guides/?${params}`);
    return response.data.results || [];
  }

  async getGuide(guideId) {
    const response = await api.get(`/immigration/guides/${guideId}/`);
    return response.data;
  }

  // FAQs
  async getFAQs(filters = {}) {
    const params = new URLSearchParams();
    if (filters.country) params.append('country', filters.country);
    if (filters.visa_type) params.append('visa_type', filters.visa_type);
    if (filters.category) params.append('category', filters.category);
    
    const response = await api.get(`/immigration/faqs/?${params}`);
    return response.data.results || [];
  }

  // Overview and Search
  async getImmigrationOverview() {
    const response = await api.get('/immigration/overview/');
    return response.data;
  }

  async searchImmigrationInfo(query) {
    const response = await api.get(`/immigration/search/?q=${encodeURIComponent(query)}`);
    return response.data;
  }

  // Utility methods
  getFlagImage(countryCode, width = 'w640') {
    const code = (countryCode || 'un').toString().toLowerCase();
    return `https://flagcdn.com/${width}/${code}.png`;
  }
  getCountryFlag(countryCode) {
    const flags = {
      'GB': '🇬🇧',
      'AU': '🇦🇺',
      'CA': '🇨🇦',
      'NZ': '🇳🇿',
      'US': '🇺🇸',
      'DE': '🇩🇪'
    };
    return flags[countryCode] || '🌍';
  }

  getPriorityColor(priority) {
    const colors = {
      'urgent': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };
    return colors[priority] || colors.medium;
  }

  getCategoryColor(category) {
    const colors = {
      'study': 'bg-blue-100 text-blue-800',
      'work': 'bg-green-100 text-green-800',
      'business': 'bg-purple-100 text-purple-800',
      'family': 'bg-pink-100 text-pink-800',
      'tourist': 'bg-yellow-100 text-yellow-800',
      'permanent': 'bg-indigo-100 text-indigo-800',
      'citizenship': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.study;
  }

  getDifficultyColor(difficulty) {
    const colors = {
      'easy': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'hard': 'bg-red-100 text-red-800'
    };
    return colors[difficulty] || colors.medium;
  }

  formatContent(content) {
    if (!content) return '';
    
    // Convert line breaks to HTML
    return content
      .replace(/\n/g, '<br>')
      .replace(/📋/g, '<span class="text-blue-600">📋</span>')
      .replace(/🔑/g, '<span class="text-yellow-600">🔑</span>')
      .replace(/📝/g, '<span class="text-green-600">📝</span>')
      .replace(/💰/g, '<span class="text-green-600">💰</span>')
      .replace(/⏰/g, '<span class="text-blue-600">⏰</span>')
      .replace(/ℹ️/g, '<span class="text-gray-600">ℹ️</span>');
  }

  getVisaImage(category, countryCode) {
    // Always use country flag image
    return this.getFlagImage(countryCode);
  }

  getPolicyImage(priority, countryCode) {
    // Always use country flag image
    return this.getFlagImage(countryCode);
  }

  getPlaceholderImage(type = 'country', countryCode = 'un') {
    // Use a flag as the only placeholder
    return this.getFlagImage(countryCode);
  }

  handleImageError(event, fallbackTypeOrCountry = 'un') {
    // Handle image loading errors by setting a flag placeholder
    const target = event.target;
    const cc = (typeof fallbackTypeOrCountry === 'string' && fallbackTypeOrCountry.length === 2)
      ? fallbackTypeOrCountry
      : 'un';
    target.src = this.getPlaceholderImage('country', cc);
    target.alt = 'Image not available';
    target.onerror = null; // Prevent infinite loop
  }

}

export default new ImmigrationConsultancyService();
